import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  Layers,
  MapPin,
  Settings2,
  Ticket,
  CheckCircle2,
  AlertTriangle,
  Armchair,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/dashboard/help/venues")({
  head: () => ({
    meta: [
      { title: "Cómo ligar un venue a un evento — ZYNTRA" },
      {
        name: "description",
        content:
          "Guía paso a paso para administradores: crear un venue, configurar zonas y asientos, y vincularlo a un evento.",
      },
    ],
  }),
  component: HelpVenuesPage,
});

function Step({
  number,
  icon: Icon,
  title,
  children,
}: {
  number: number;
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-2xl border border-border bg-card p-6">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full gradient-primary text-sm font-black text-primary-foreground">
          {number}
        </div>
        <Icon className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-bold text-card-foreground">{title}</h3>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  );
}

function HelpVenuesPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 pt-24 pb-20 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver al dashboard
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-black text-foreground">Cómo ligar un venue a un evento</h1>
        <p className="mt-2 text-base text-muted-foreground">
          Guía completa para administradores y organizadores. Aprendé a crear un venue, configurar
          sus zonas (general o numerada) y vincularlo a un evento listo para vender entradas.
        </p>
      </div>

      {/* Conceptos */}
      <div className="mb-8 rounded-2xl border border-primary/20 bg-primary/5 p-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary">
          <Layers className="h-4 w-4" />
          Conceptos clave
        </h2>
        <ul className="space-y-2 text-sm text-foreground">
          <li>
            <strong>Venue:</strong> el lugar físico (estadio, club, teatro). Reusable entre eventos.
          </li>
          <li>
            <strong>Mapa del venue:</strong> el plano visual con sus zonas dibujadas.
          </li>
          <li>
            <strong>Zona general:</strong> tiene capacidad total, sin asientos asignados (ej: pista,
            campo).
          </li>
          <li>
            <strong>Zona numerada:</strong> tiene filas y asientos individuales que el comprador
            elige (ej: platea, palco).
          </li>
          <li>
            <strong>Reglas comerciales:</strong> precio, máximo por usuario y reventa se definen{" "}
            <em>por evento</em> en cada zona — no en el venue.
          </li>
        </ul>
      </div>

      <div className="space-y-4">
        <Step number={1} icon={Building2} title="Crear el venue">
          <p>
            Andá a <strong>Dashboard → Venues → Crear venue</strong>. Completá nombre, ciudad y
            dirección. Si vas a reusar el venue para múltiples eventos, dejá activo{" "}
            <em>“reusable”</em>.
          </p>
          <p>
            Al guardar, el sistema crea automáticamente un <strong>mapa</strong> vacío asociado al
            venue.
          </p>
        </Step>

        <Step number={2} icon={MapPin} title="Dibujar las zonas en el mapa">
          <p>
            Entrá al venue recién creado y abrí el editor de mapa. Dibujá las zonas que necesites
            (rectángulos sobre el plano). Cada zona necesita:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>Nombre (ej: “Platea Alta”, “Pista”)</li>
            <li>
              Tipo:{" "}
              <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs">
                <Users className="h-3 w-3" /> General
              </span>{" "}
              o{" "}
              <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs">
                <Armchair className="h-3 w-3" /> Numerada
              </span>
            </li>
            <li>Color para identificarla visualmente</li>
            <li>Capacidad total (solo si es general)</li>
          </ul>
          <p className="flex items-start gap-2 rounded-lg bg-warning/10 p-3 text-warning-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              <strong>Importante:</strong> guardá la zona <em>antes</em> de configurar filas y
              asientos. El botón “Editar filas y asientos” solo aparece después de guardar.
            </span>
          </p>
        </Step>

        <Step number={3} icon={Armchair} title="(Solo numeradas) Generar filas y asientos">
          <p>
            Si la zona es <strong>numerada</strong>, hacé click en <em>“Editar filas y asientos”</em>{" "}
            y usá el generador automático:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>Cantidad de filas (ej: 10)</li>
            <li>Etiqueta inicial de fila (A, B, C…)</li>
            <li>Asientos por fila (ej: 20)</li>
          </ul>
          <p>
            El sistema crea automáticamente los asientos <code>A1, A2, …, J20</code> con etiquetas
            únicas. Podés editarlos manualmente después.
          </p>
        </Step>

        <Step number={4} icon={Ticket} title="Crear el evento">
          <p>
            Andá a <strong>Dashboard → Crear evento</strong> y completá los datos básicos: título,
            fecha, hora, ciudad, descripción, imagen. Por ahora <strong>no</strong> elijas venue
            todavía — guardá el evento.
          </p>
        </Step>

        <Step number={5} icon={Settings2} title="Ligar el venue al evento">
          <p>
            Desde el dashboard, abrí el evento y entrá a{" "}
            <strong>“Asignar venue”</strong>. Vas a ver:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Selector de venue:</strong> elegí uno de tus venues. Solo aparecen los que
              tienen mapa configurado.
            </li>
            <li>
              <strong>Lista de zonas:</strong> al elegir el venue, se cargan automáticamente todas
              las zonas activas del mapa.
            </li>
          </ul>
          <p>
            Para cada zona configurá las <strong>reglas comerciales del evento</strong>:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Precio</strong> en la moneda del tenant (ARS / CRC)
            </li>
            <li>
              <strong>Máximo por usuario</strong> (ej: 4 entradas)
            </li>
            <li>
              <strong>Visible al público</strong> — desactivá para zonas internas/cortesía
            </li>
            <li>
              <strong>Permite reventa</strong> — controla si los compradores pueden revender
            </li>
          </ul>
          <p>
            Hacé click en <em>“Guardar configuración”</em>.
          </p>
        </Step>

        <Step number={6} icon={CheckCircle2} title="Publicar y vender">
          <p>
            Volvé al evento y marcalo como <strong>publicado</strong>. Los compradores verán:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>El mapa visual con las zonas</li>
            <li>
              Si la zona es <strong>general</strong>: selector de cantidad
            </li>
            <li>
              Si la zona es <strong>numerada</strong>: <em>SeatPicker</em> con la grilla de asientos
              disponibles
            </li>
          </ul>
          <p>
            El sistema gestiona automáticamente los <em>holds</em> (15 min de reserva), checkout con
            Stripe, y emisión de tickets con QR.
          </p>
        </Step>
      </div>

      {/* FAQ */}
      <div className="mt-10">
        <h2 className="mb-4 text-xl font-black text-foreground">Preguntas frecuentes</h2>
        <div className="space-y-3">
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer text-sm font-bold text-card-foreground">
              ¿Puedo reusar un venue en varios eventos?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Sí. Si activaste <em>“reusable”</em> al crear el venue, podés ligarlo a tantos eventos
              como quieras. Las reglas comerciales (precio, etc.) se configuran por evento.
            </p>
          </details>
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer text-sm font-bold text-card-foreground">
              ¿Qué pasa si cambio el mapa después de vender entradas?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              No se recomienda. Los tickets emitidos referencian zonas y asientos específicos.
              Cambiar el mapa puede romper la trazabilidad. Si necesitás cambios, creá un mapa
              nuevo (versionado) en lugar de editar el existente.
            </p>
          </details>
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer text-sm font-bold text-card-foreground">
              ¿Cómo combino zonas generales y numeradas en el mismo evento?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              En el mismo mapa podés tener ambos tipos. Por ejemplo: “Pista” (general, 1000 cap.) +
              “Platea” (numerada, 200 asientos). El comprador verá el flujo correcto según la zona
              que elija.
            </p>
          </details>
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer text-sm font-bold text-card-foreground">
              ¿Por qué no aparece mi venue en el selector?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              El selector solo muestra venues que tengan al menos un <strong>mapa</strong>{" "}
              configurado. Si recién lo creaste, asegurate de haber dibujado al menos una zona y
              guardado.
            </p>
          </details>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/dashboard/venues/new"
          className="inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-3 text-sm font-bold text-primary-foreground glow-primary"
        >
          <Building2 className="h-4 w-4" />
          Crear un venue
        </Link>
        <Link
          to="/create-event"
          className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-3 text-sm font-bold text-card-foreground hover:bg-secondary"
        >
          <Ticket className="h-4 w-4" />
          Crear un evento
        </Link>
      </div>
    </div>
  );
}
